import { Component, OnDestroy, OnInit, ViewChild, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthService } from 'src/app/core/authentication/auth.service';
import { Subscription, filter, finalize, first, switchMap } from 'rxjs';
import { AgreementToLogWork } from 'src/app/models/dtos/agreement-to-log-work';
import { WorkerAgreementService } from 'src/app/data/worker-agreement.service'; // Ensure this is correctly imported
import { WorkerService } from 'src/app/data/worker.service';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { DatePipe } from '@angular/common';
import { TranslateService } from '@ngx-translate/core';
import { NgxSpinnerService } from 'ngx-spinner';
import { TimesheetService } from 'src/app/data/timesheet.service';
import { SnackBarService } from 'src/app/shared/services/snack-bar.service';
import { MatSelect } from '@angular/material/select';
import { Messages } from 'src/app/common/enums';
import { LogWorkRequest } from 'src/app/contracts/requests/log-work.request';
import { LogWorkState } from 'src/app/common/enums/log-work-state';

const validErrorCodes = [Messages.QrCodeExpired, Messages.QrCodeInvalid, Messages.Exceeded24HoursWorkLog, Messages.TwoEventsOverlap, Messages.TimesheetIsSettled];

@Component({
  selector: 'app-log-work',
  templateUrl: './log-work.component.html',
  styleUrls: ['./log-work.component.scss'],
  providers: [DatePipe],
  standalone: false
})
export class LogWorkComponent implements OnDestroy, OnInit {
  private router = inject(Router);
  private authService = inject(AuthService);
  private route = inject(ActivatedRoute);
  private workerAgreementService = inject(WorkerAgreementService);
  private workerService = inject(WorkerService);
  private formBuilder = inject(UntypedFormBuilder);
  private translateService = inject(TranslateService);
  private spinner = inject(NgxSpinnerService);
  private timesheetService = inject(TimesheetService);
  private snackBarService = inject(SnackBarService);
  private datePipe = inject(DatePipe);

  workerId: number;
  qrCode: string;
  private routeSub: Subscription;
  LogWorkState = LogWorkState;
  state: LogWorkState = LogWorkState.NONE;

  employerName: string;
  registrationText: string = '';
  countdownText: string = '';
  successMessage: string = '';
  errorMessage: string = '';
  formGroup: UntypedFormGroup;
  selectedAgreement: AgreementToLogWork;
  agreements: AgreementToLogWork[];

  private dateNow = new Date();
  private countdownInSeconds: number = 5;
  private oneSecondInMiliseconds: number = 1000;
  private cancelInterval: boolean = false;

  private date: string;
  private time: string;
  private interval: ReturnType<typeof setTimeout>;

  @ViewChild('matSelect') matSelect: MatSelect;

  constructor() { }

  ngOnInit(): void {
    this.routeSub = this.route.params.subscribe(params => {
      this.qrCode = params['code'];
    });

    this.authService.userData$
      .pipe(
        filter(user => !!user.userData),
        first(),
        switchMap(user => this.workerService.getWorkerIdByAuthServerUserId(user.userData.authServerUserId)),
        switchMap(workerId => {
          this.workerId = workerId;
          return this.workerAgreementService.getActiveWorkerAgreementsByQrCode(this.workerId, this.qrCode);
        })
      )
      .subscribe(agreements => {
        this.agreements = agreements;
        this.handleAgreementList();
      },
        error => {
          this.state = LogWorkState.ERROR;
          this.errorMessage = error.error;
        }
      );

    this.setDateAndTime();
    this.buildFormGroup();
    this.onAgreementToLogWorkChanges();
  }

  redirectToTimesheets(): void {
    this.redirectTo(`profile/${this.workerId}/timesheets`);
  }

  redirectToProfile(): void {
    this.redirectTo(`profile/${this.workerId}`);
  }

  redirectTo(uri: string): void {
    this.router.navigate([uri]);
  }

  ngOnDestroy() {
    this.routeSub && this.routeSub.unsubscribe();
    this.clearInterval();
  }

  private startCountdown(): void {
    this.clearInterval();
    this.cancelInterval = false;

    let countdown = this.countdownInSeconds;
    this.updateCountdownText(countdown);

    this.interval = setInterval(() => {
      countdown--;
      this.updateCountdownText(countdown);

      if (countdown === 0) {
        this.onCountdownFinish();
      }
    }, this.oneSecondInMiliseconds);
  }

  private updateCountdownText(countdown: number) {
    this.countdownText = this.translateService.instant('LogWork.AutomaticRegistrationCounter', { countdown });
  }

  private onCountdownFinish() {
    this.clearInterval();

    if (!this.cancelInterval) {
      this.submit();
    }
  }

  submit(changeStartStop: boolean = false, cancelInterval: boolean = false) {
    this.cancelInterval = cancelInterval;
    clearInterval(this.interval);

    this.createOrUpdateTimesheetRecord(this.createRequest(changeStartStop));
  }

  createRequest(changeStartStop: boolean): LogWorkRequest {
    return {
      WorkerId: this.workerId,
      AgreementId: this.selectedAgreement.Id,
      QrCode: this.qrCode,
      TimesheetRecordId: this.selectedAgreement.ActiveTimesheetRecordId,
      IsWorkStart: (!this.selectedAgreement.ActiveTimesheetRecordId && !changeStartStop) || (!!this.selectedAgreement.ActiveTimesheetRecordId && changeStartStop)
    }
  }

  private createOrUpdateTimesheetRecord(request: LogWorkRequest) {
    const action$ = !request.IsWorkStart && this.selectedAgreement.ActiveTimesheetRecordId ?
      this.timesheetService.updateLogWorkEntry(request) :
      this.timesheetService.createLogWorkEntry(request);

    const message = !request.IsWorkStart && this.selectedAgreement.ActiveTimesheetRecordId ? Messages.SuccessfullyUpdatedTimesheet : Messages.SuccessfullyCreatedTimesheet;

    this.spinner.show();

    action$.pipe(first()).subscribe({
      next: (success) => {
        this.spinner.hide();
        this.state = LogWorkState.SUCCESS;
        this.successMessage = message;
      },
      error: (error) => {
        this.spinner.hide();
        this.state = LogWorkState.ERROR;

        if (validErrorCodes.includes(error.error)) {
          this.errorMessage = error.error;
        } else {
          this.snackBarService.openErrorSnackBar(Messages.SomethingWentWrong);
        }
      },
    });
  }

  private onAgreementToLogWorkChanges() {
    this.formGroup.get('activeAgreement').valueChanges.subscribe((agreementId) => {
      const selectedAgreement = this.agreements.find((agreement) => agreement.Id === agreementId);
      if (selectedAgreement) {
        this.onAgreementSelect(selectedAgreement);
      }
    });
  }

  private handleAgreementList() {
    if (this.agreements.length === 0) {
      this.state = LogWorkState.ERROR;
      this.snackBarService.openErrorSnackBar(Messages.NoAgreementsToLogWorkTime);
    } else if (this.agreements.length === 1) {
      this.formGroup.get('activeAgreement')?.setValue(this.agreements[0].Id, { emitEvent: true });
      this.onAgreementSelect(this.agreements[0]);
    } else if (this.agreements.length > 1) {
      setTimeout(() => {
        this.matSelect.focus();
        this.matSelect.open();
      }, 0);
    }
  }

  private buildFormGroup(): void {
    this.formGroup = this.formBuilder.group({
      activeAgreement: [null, [Validators.required]],
    });
  }

  private onAgreementSelect(agreement: AgreementToLogWork) {
    this.startCountdown();
    this.selectedAgreement = agreement;
    let translationKey = agreement.ActiveTimesheetRecordId ? 'LogWork.RegistrateEndWorkText' : 'LogWork.RegistrateStartWorkText';
    this.employerName = agreement.EmployerName;

    this.registrationText = this.translateService.instant(translationKey, { date: this.date, time: this.time });
  }

  cancel() {
    this.cancelInterval = true;
    this.clearInterval();
    this.state = LogWorkState.CANCELLED;
  }

  private clearInterval() {
    if (this.interval) {
      clearInterval(this.interval);
    }
  }

  private setDateAndTime() {
    this.date = this.datePipe.transform(this.dateNow, 'dd.MM.yyyy');
    this.time = this.datePipe.transform(this.dateNow, 'HH:mm');
  }
}
